import { useState, useCallback, useMemo } from 'react';
import { getParentIds, parseNodeId } from '../utils/treeHelpers';

/**
 * Hook for managing tree data structure and filtering
 */
export function useTreeData(prepFiles = []) {
  const [treeSelection, setTreeSelection] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedNodes, setSelectedNodes] = useState([]);
  const [expandedItems, setExpandedItems] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filterCriteria, setFilterCriteria] = useState(null);
  
  // Build full tree hierarchy from prep files
  const buildTreeData = useCallback(() => {
    const projects = {};
    
    prepFiles.forEach(prep => {
      const project = prep.project;
      const target = prep.target;
      const batch = prep.batch;
      
      if (!projects[project]) {
        projects[project] = {
          id: `project::${project}`,
          label: project,
          targets: {}
        };
      }
      
      if (!projects[project].targets[target]) {
        projects[project].targets[target] = {
          id: `target::${project}::${target}`,
          label: target,
          batches: {}
        };
      }
      
      if (!projects[project].targets[target].batches[batch]) {
        projects[project].targets[target].batches[batch] = {
          id: `batch::${project}::${target}::${batch}`,
          label: batch,
          items: [],
          earliestDate: prep.prepared_at || prep.created_at
        };
      } else {
        // Update earliest date if this prep has an earlier date
        const prepDate = prep.prepared_at || prep.created_at;
        if (prepDate && (!projects[project].targets[target].batches[batch].earliestDate || 
            prepDate < projects[project].targets[target].batches[batch].earliestDate)) {
          projects[project].targets[target].batches[batch].earliestDate = prepDate;
        }
      }
      
      projects[project].targets[target].batches[batch].items.push(prep);
    });
    
    return projects;
  }, [prepFiles]);

  // Memoize tree data
  const treeData = useMemo(() => buildTreeData(), [buildTreeData]);

  // Apply tree filter to data (supports multiple selections)
  const applyTreeFilter = useCallback((nodeIds, autoSelect = false) => {
    // Handle single node or array of nodes
    const nodeIdArray = Array.isArray(nodeIds) ? nodeIds : (nodeIds ? [nodeIds] : []);

    if (!nodeIdArray.length) {
      setFilteredData(prepFiles);
      setFilterCriteria(null);
      return { filtered: prepFiles, shouldAutoSelect: false };
    }

    // For single selection, keep existing behavior
    if (nodeIdArray.length === 1) {
      const nodeId = nodeIdArray[0];
      let filtered = [];
      let criteria = null;

      const parsed = parseNodeId(nodeId);
      if (parsed.type === 'project') {
        filtered = prepFiles.filter(prep => prep.project === parsed.project);
        criteria = { project: parsed.project };
      } else if (parsed.type === 'target') {
        filtered = prepFiles.filter(prep =>
          prep.project === parsed.project && prep.target === parsed.target
        );
        criteria = { project: parsed.project, target: parsed.target };
      } else if (parsed.type === 'batch') {
        filtered = prepFiles.filter(prep =>
          prep.project === parsed.project &&
          prep.target === parsed.target &&
          prep.batch === parsed.batch
        );
        criteria = { project: parsed.project, target: parsed.target, batch: parsed.batch };
      }

      setFilteredData(filtered);
      setFilterCriteria(criteria);
      setTreeSelection(nodeIdArray);
      setSelectedNode(nodeId); // Keep for backward compatibility

      return {
        filtered,
        shouldAutoSelect: autoSelect,
        parsedNode: parsed,
        parsedNodes: [parsed]
      };
    }

    // Multiple selection - combine results from different node types
    const parsedNodes = nodeIdArray.map(id => parseNodeId(id));

    // Filter to include files from any selected node (project, target, or batch)
    const filtered = prepFiles.filter(prep => {
      return parsedNodes.some(node => {
        if (node.type === 'project') {
          return prep.project === node.project;
        } else if (node.type === 'target') {
          return prep.project === node.project && prep.target === node.target;
        } else if (node.type === 'batch') {
          return prep.project === node.project &&
                 prep.target === node.target &&
                 prep.batch === node.batch;
        }
        return false;
      });
    });

    // Store mixed selection criteria
    const criteria = {
      mixedNodes: parsedNodes,
      // Keep track of specific batch nodes for backward compatibility
      batches: parsedNodes.filter(node => node.type === 'batch')
    };

    setFilteredData(filtered);
    setFilterCriteria(criteria);
    setTreeSelection(nodeIdArray);
    setSelectedNode(null); // Clear single selection for multi-select

    return {
      filtered,
      shouldAutoSelect: autoSelect,
      parsedNodes
    };
  }, [prepFiles]);

  // Expand tree to show a specific node
  const expandToNode = useCallback((nodeId) => {
    if (!nodeId) return;
    
    // Get all parent nodes that need to be expanded
    const nodesToExpand = getParentIds(nodeId);
    
    // Update expanded items, keeping existing expansions
    setExpandedItems(prev => {
      const newExpanded = [...prev];
      nodesToExpand.forEach(node => {
        if (!newExpanded.includes(node)) {
          newExpanded.push(node);
        }
      });
      return newExpanded;
    });
  }, []);

  // Handle tree selection change (supports multiple selections)
  const handleTreeSelectionChange = useCallback((_, nodeIds) => {
    // nodeIds is now an array for multi-selection
    setTreeSelection(nodeIds);
    setSelectedNodes(nodeIds);

    // Maintain backward compatibility for single selection
    if (nodeIds && nodeIds.length === 1) {
      setSelectedNode(nodeIds[0]);
    } else {
      setSelectedNode(null);
    }

    if (!nodeIds || nodeIds.length === 0) {
      setFilteredData(prepFiles);
      return { filtered: prepFiles, parsedNode: null, parsedNodes: [] };
    }

    // Apply filter with auto-select
    const result = applyTreeFilter(nodeIds, true);
    return result;
  }, [prepFiles, applyTreeFilter]);

  // Clear tree filter
  const clearTreeFilter = useCallback(() => {
    setTreeSelection([]);
    setSelectedNode(null);
    setSelectedNodes([]);
    setFilteredData(prepFiles);
    setFilterCriteria(null);
  }, [prepFiles]);

  // Restore filter after data refresh
  const restoreFilter = useCallback(() => {
    if (filterCriteria) {
      // Handle mixed node criteria (projects, targets, batches)
      if (filterCriteria.mixedNodes) {
        // Filter based on mixed nodes
        const filtered = prepFiles.filter(prep => {
          return filterCriteria.mixedNodes.some(node => {
            if (node.type === 'project') {
              return prep.project === node.project;
            } else if (node.type === 'target') {
              return prep.project === node.project && prep.target === node.target;
            } else if (node.type === 'batch') {
              return prep.project === node.project &&
                     prep.target === node.target &&
                     prep.batch === node.batch;
            }
            return false;
          });
        });

        setFilteredData(filtered);

        // Reconstruct tree selection
        const nodeIds = filterCriteria.mixedNodes.map(node => {
          if (node.type === 'project') {
            return `project::${node.project}`;
          } else if (node.type === 'target') {
            return `target::${node.project}::${node.target}`;
          } else if (node.type === 'batch') {
            return `batch::${node.project}::${node.target}::${node.batch}`;
          }
          return null;
        }).filter(id => id !== null);
        setTreeSelection(nodeIds);
      } else {
        // Legacy single selection criteria
        let filtered = prepFiles;

        if (filterCriteria.project) {
          filtered = filtered.filter(prep => prep.project === filterCriteria.project);
        }
        if (filterCriteria.target) {
          filtered = filtered.filter(prep => prep.target === filterCriteria.target);
        }
        if (filterCriteria.batch) {
          filtered = filtered.filter(prep => prep.batch === filterCriteria.batch);
        }

        setFilteredData(filtered);

        // Try to reconstruct the tree selection if possible
        if (filterCriteria.batches) {
          // Multiple batches stored
          const nodeIds = filterCriteria.batches.map(batch =>
            `batch::${batch.project}::${batch.target}::${batch.batch}`
          );
          setTreeSelection(nodeIds);
        } else if (filterCriteria.batch) {
          setTreeSelection([`batch::${filterCriteria.project}::${filterCriteria.target}::${filterCriteria.batch}`]);
        } else if (filterCriteria.target) {
          setTreeSelection([`target::${filterCriteria.project}::${filterCriteria.target}`]);
        } else if (filterCriteria.project) {
          setTreeSelection([`project::${filterCriteria.project}`]);
        }
      }
    } else if (treeSelection) {
      // Fall back to tree selection if no criteria stored
      applyTreeFilter(treeSelection);
    } else {
      setFilteredData(prepFiles);
    }
  }, [prepFiles, filterCriteria, treeSelection, applyTreeFilter]);

  return {
    // State
    treeData,
    treeSelection,
    setTreeSelection,
    selectedNode,
    setSelectedNode,
    selectedNodes,
    setSelectedNodes,
    expandedItems,
    setExpandedItems,
    filteredData,
    setFilteredData,
    filterCriteria,
    setFilterCriteria,

    // Methods
    buildTreeData,
    applyTreeFilter,
    expandToNode,
    handleTreeSelectionChange,
    clearTreeFilter,
    restoreFilter
  };
}